import config from "@/libs/config";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import { css } from "@emotion/react";
import { useForm, FormProvider } from "react-hook-form";
import { MainTemplate } from "@/components/templates";
import { PageTitle } from "@/components/molecules";
import { UpdatePasswordForm } from "@/components/organisms";
import { Font, useSnackBar } from "@moneyforward/mfx_biz_react_components";
import {
  useVerifyForgotPasswordTokenMutation,
  VerifyForgotPasswordTokenMutation,
  useUpdatePasswordWithTokenMutation,
  UpdatePasswordWithTokenMutation,
} from "@/generated/graphql";
import { getUrlWithQuery } from "@/libs/url";
import { clearAegisTokens } from "@/libs/aegisSession";
import useRollbarUtil from "../hooks/useRollbarUtil";

type FormValues = {
  password: string;
};

export const UpdatePasswordPage = () => {
  const { getNotifyErrorHandlerForGraphQL } = useRollbarUtil();
  const router = useRouter();
  const [verifying, setVerifying] = useState(true);
  const token = useMemo(() => {
    return router.query.token as string;
  }, [router.query.token]);
  const { app } = router.query;

  const { notify } = useSnackBar();

  const [verifyForgotPasswordTokenMutation] =
    useVerifyForgotPasswordTokenMutation({
      onCompleted: (data: VerifyForgotPasswordTokenMutation) => {
        if (!data.verifyForgotPasswordToken) {
          notify("メール認証に失敗しました");
          return;
        }
        setVerifying(false);
      },
      onError: getNotifyErrorHandlerForGraphQL(
        "Failed to verify forgot password token",
        "メール認証に失敗しました"
      ),
    });

  const [updatePasswordWithToken, { loading: updatePasswordWithTokenLoading }] =
    useUpdatePasswordWithTokenMutation({
      onCompleted: (data: UpdatePasswordWithTokenMutation) => {
        if (!data.updatePasswordWithToken) {
          notify("パスワードの変更に失敗しました");
          return;
        }
        notify("パスワードを変更しました");
        // Clear the frontend session so the sign-in page doesn't detect an
        // active session and bounce the user to the Service List.
        clearAegisTokens();
        localStorage.removeItem("session-user");
        router.push(getUrlWithQuery("/sign-in", app));
      },
      onError: getNotifyErrorHandlerForGraphQL(
        "Failed to update password with token",
        "パスワードの変更に失敗しました"
      ),
    });

  const methods = useForm<FormValues>({
    reValidateMode: "onBlur",
    defaultValues: {
      password: "",
    },
  });

  const handleSubmit = useCallback(
    async ({ password }: FormValues) => {
      if (updatePasswordWithTokenLoading) return;
      await updatePasswordWithToken({ variables: { token, password } });
    },
    [updatePasswordWithToken, token]
  );

  useEffect(() => {
    if (!token) return;
    verifyForgotPasswordTokenMutation({ variables: { input: { token } } });
  }, [token]);

  return (
    <MainTemplate title={`パスワード変更`}>
      {verifying ? (
        <div css={messageWrapper}>
          <Font variant="m" component="p">
            確認中...
          </Font>
        </div>
      ) : (
        <>
          <PageTitle
            mainTitle={`パスワード変更`}
            subTitle={
              !config.USING_BIZSOL && (
                <>変更後の新しいパスワードを入力してください</>
              )
            }
          />
          <FormProvider {...methods}>
            <UpdatePasswordForm onSubmit={handleSubmit} />
          </FormProvider>
        </>
      )}
    </MainTemplate>
  );
};

const messageWrapper = css({
  display: "flex",
  justifyContent: "center",
});
